/**
 * NUTRITION AI SERVICE
 * KI-gestützter Ernährungs-Coach für Clara Vital Center
 * Integration mit Samsung Health für optimale Ernährungsempfehlungen
 */

class NutritionAIService {
  constructor() {
    this.apiKey = process.env.OPENAI_API_KEY;
    this.apiBase = process.env.OPENAI_API_BASE;
    this.initialized = false;
  }

  async initialize() {
    try {
      this.initialized = true;
      console.log('✅ Nutrition AI Service initialized');
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize Nutrition AI Service:', error);
      return false;
    }
  }

  // FOTO-BASIERTE MAHLZEIT-ANALYSE
  async analyzeMealPhoto(imageBase64, userGoals = {}) {
    try {
      // Mock-Implementierung für Demo
      const mockAnalysis = {
        detectedFoods: [
          {
            name: 'Gegrilltes Hähnchen',
            quantity: '150g',
            calories: 248,
            protein: 46.5,
            carbs: 0,
            fat: 5.4,
            confidence: 0.92
          },
          {
            name: 'Quinoa Salat',
            quantity: '100g',
            calories: 120,
            protein: 4.4,
            carbs: 22,
            fat: 1.9,
            confidence: 0.87
          },
          {
            name: 'Avocado',
            quantity: '50g',
            calories: 80,
            protein: 1,
            carbs: 4.5,
            fat: 7.5,
            confidence: 0.95
          }
        ],
        totalNutrition: {
          calories: 448,
          protein: 51.9,
          carbs: 26.5,
          fat: 14.8,
          fiber: 8.2
        },
        healthScore: 92,
        recommendations: [
          'Ausgezeichnete Proteinquelle für Muskelaufbau',
          'Komplexe Kohlenhydrate für nachhaltige Energie',
          'Gesunde Fette für optimale Nährstoffaufnahme'
        ],
        mealTiming: this.getMealTimingRecommendation(),
        macroBalance: this.calculateMacroBalance(51.9, 26.5, 14.8)
      };

      return mockAnalysis;
    } catch (error) {
      console.error('❌ Meal photo analysis failed:', error);
      return null;
    }
  }

  // KI-BASIERTE MEAL-PLAN GENERIERUNG
  async generateMealPlan(userProfile, fitnessGoals, healthData = {}) {
    try {
      const { 
        dailyCalories = 2000, 
        proteinTarget = 150, 
        dietaryRestrictions = [],
        workoutSchedule = []
      } = userProfile;

      // Samsung Health Integration
      const caloriesBurned = healthData.caloriesBurned || 0;
      const adjustedCalories = dailyCalories + (caloriesBurned * 0.7); // 70% der verbrannten Kalorien zurück

      const mealPlan = {
        planName: '7-Tage KI-Optimierter Plan',
        totalCalories: Math.round(adjustedCalories),
        targetMacros: {
          protein: proteinTarget,
          carbs: Math.round(adjustedCalories * 0.45 / 4), // 45% Kohlenhydrate
          fat: Math.round(adjustedCalories * 0.25 / 9)    // 25% Fette
        },
        meals: this.generateWeeklyMeals(adjustedCalories, proteinTarget, workoutSchedule),
        shoppingList: this.generateShoppingList(),
        prepTips: this.generateMealPrepTips(),
        aiInsights: this.generateNutritionInsights(userProfile, healthData)
      };

      return mealPlan;
    } catch (error) {
      console.error('❌ Meal plan generation failed:', error);
      return null;
    }
  }

  // SAMSUNG HEALTH INTEGRATION
  async syncWithSamsungHealth(healthData) {
    try {
      const nutritionRecommendations = {
        preWorkout: this.getPreWorkoutNutrition(healthData),
        postWorkout: this.getPostWorkoutNutrition(healthData),
        hydration: this.calculateHydrationNeeds(healthData),
        sleepOptimization: this.getSleepNutritionTips(healthData),
        stressManagement: this.getStressNutritionTips(healthData)
      };

      return nutritionRecommendations;
    } catch (error) {
      console.error('❌ Samsung Health sync failed:', error);
      return null;
    }
  }

  // REAL-TIME ERNÄHRUNGS-COACHING
  async getRealtimeCoaching(currentMeals, userGoals, healthData) {
    try {
      const coaching = {
        currentStatus: this.analyzeCurrentIntake(currentMeals, userGoals),
        nextMealSuggestion: this.suggestNextMeal(currentMeals, userGoals, healthData),
        hydrationReminder: this.getHydrationReminder(healthData),
        supplementTiming: this.getSupplementTiming(healthData),
        energyOptimization: this.getEnergyOptimization(currentMeals, healthData)
      };

      return coaching;
    } catch (error) {
      console.error('❌ Realtime coaching failed:', error);
      return null;
    }
  }

  // HELPER METHODS
  generateWeeklyMeals(calories, protein, workoutSchedule) {
    const dailyCalories = Math.round(calories / 3); // Vereinfacht: 3 Hauptmahlzeiten
    
    return {
      monday: {
        breakfast: {
          name: 'Protein-Overnight-Oats',
          calories: Math.round(dailyCalories * 0.3),
          protein: Math.round(protein * 0.25),
          ingredients: ['Haferflocken', 'Griechischer Joghurt', 'Beeren', 'Mandeln']
        },
        lunch: {
          name: 'Quinoa-Buddha-Bowl',
          calories: Math.round(dailyCalories * 0.4),
          protein: Math.round(protein * 0.35),
          ingredients: ['Quinoa', 'Hähnchenbrust', 'Avocado', 'Spinat']
        },
        dinner: {
          name: 'Lachs mit Süßkartoffel',
          calories: Math.round(dailyCalories * 0.3),
          protein: Math.round(protein * 0.4),
          ingredients: ['Lachs', 'Süßkartoffel', 'Brokkoli', 'Olivenöl']
        }
      }
      // Weitere Tage würden hier folgen...
    };
  }

  generateShoppingList() {
    return {
      proteins: ['Hähnchenbrust', 'Lachs', 'Griechischer Joghurt', 'Eier'],
      carbs: ['Quinoa', 'Süßkartoffeln', 'Haferflocken', 'Vollkornbrot'],
      fats: ['Avocado', 'Mandeln', 'Olivenöl', 'Nüsse'],
      vegetables: ['Spinat', 'Brokkoli', 'Paprika', 'Zucchini'],
      fruits: ['Beeren', 'Bananen', 'Äpfel'],
      supplements: ['Whey Protein', 'Omega-3', 'Multivitamin']
    };
  }

  generateMealPrepTips() {
    return [
      '🥘 Sonntags 2-3 Stunden für Meal-Prep einplanen',
      '🍚 Quinoa und Süßkartoffeln in größeren Mengen vorkochen',
      '🥗 Salate ohne Dressing vorbereiten, separat lagern',
      '🍗 Protein portionsweise einfrieren',
      '💧 Wasser-Flaschen mit Zitronen vorbereiten'
    ];
  }

  generateNutritionInsights(userProfile, healthData) {
    return [
      {
        type: 'energy_optimization',
        title: 'Energie-Optimierung',
        insight: 'Basierend auf Ihrem Schlafmuster sollten Sie komplexe Kohlenhydrate am Morgen priorisieren.',
        confidence: 87
      },
      {
        type: 'recovery_nutrition',
        title: 'Recovery-Ernährung',
        insight: 'Nach intensiven Workouts: 20-30g Protein innerhalb 30 Minuten für optimale Regeneration.',
        confidence: 94
      },
      {
        type: 'hydration_timing',
        title: 'Hydration-Timing',
        insight: 'Trinken Sie 500ml Wasser 2 Stunden vor dem Training für optimale Performance.',
        confidence: 91
      }
    ];
  }

  getMealTimingRecommendation() {
    const hour = new Date().getHours();
    
    if (hour < 10) return 'Perfekt für den Frühstück-Boost!';
    if (hour < 14) return 'Ideale Mittagsmahlzeit für nachhaltige Energie';
    if (hour < 18) return 'Guter Pre-Workout Snack';
    return 'Leichte Abendmahlzeit für besseren Schlaf';
  }

  calculateMacroBalance(protein, carbs, fat) {
    const total = protein + carbs + fat;
    return {
      proteinPercent: Math.round((protein / total) * 100),
      carbsPercent: Math.round((carbs / total) * 100),
      fatPercent: Math.round((fat / total) * 100),
      balance: 'Ausgewogen für Muskelaufbau und Energie'
    };
  }

  getPreWorkoutNutrition(healthData) {
    return {
      timing: '30-60 Minuten vor dem Training',
      foods: ['Banane mit Mandelbutter', 'Haferflocken mit Beeren'],
      hydration: '300-500ml Wasser',
      supplements: ['Koffein (optional)', 'BCAAs']
    };
  }

  getPostWorkoutNutrition(healthData) {
    return {
      timing: 'Innerhalb 30 Minuten nach dem Training',
      foods: ['Protein-Shake mit Banane', 'Griechischer Joghurt mit Beeren'],
      hydration: '150% der verlorenen Flüssigkeit',
      supplements: ['Whey Protein', 'Kreatin']
    };
  }

  calculateHydrationNeeds(healthData) {
    const baseWater = 35; // ml pro kg Körpergewicht
    const weight = healthData.weight || 70;
    const caloriesBurned = healthData.caloriesBurned || 0;
    
    const baseNeeds = weight * baseWater;
    const exerciseNeeds = caloriesBurned * 0.5; // 0.5ml pro verbrannte Kalorie
    
    return {
      dailyTarget: Math.round(baseNeeds + exerciseNeeds),
      currentIntake: healthData.waterIntake || 0,
      remaining: Math.round(baseNeeds + exerciseNeeds - (healthData.waterIntake || 0)),
      tips: [
        'Trinken Sie alle 15-20 Minuten während des Trainings',
        'Urin sollte hellgelb bis durchsichtig sein',
        'Bei Durst sind Sie bereits dehydriert'
      ]
    };
  }

  getSleepNutritionTips(healthData) {
    const sleepQuality = healthData.sleepScore || 70;
    
    if (sleepQuality < 70) {
      return [
        '🌙 Magnesium 2 Stunden vor dem Schlafengehen',
        '🚫 Koffein nach 14 Uhr vermeiden',
        '🥛 Warme Milch mit Honig als Schlaftrunk',
        '🍒 Kirschen enthalten natürliches Melatonin'
      ];
    }
    
    return ['✅ Ihre Schlafernährung ist optimal!'];
  }

  getStressNutritionTips(healthData) {
    const stressLevel = healthData.stressLevel || 'normal';
    
    if (stressLevel === 'high') {
      return [
        '🫐 Antioxidantien-reiche Beeren gegen Stress',
        '🐟 Omega-3 Fettsäuren für Gehirngesundheit',
        '🥑 Avocado für gesunde Fette und B-Vitamine',
        '🍃 Grüner Tee statt Kaffee für ruhige Energie'
      ];
    }
    
    return ['✅ Ihr Stress-Level ist im normalen Bereich'];
  }

  // MOCK DATA METHODS
  getMockNutritionData() {
    return {
      todaysMeals: {
        breakfast: { calories: 420, protein: 25, carbs: 45, fat: 12 },
        lunch: { calories: 580, protein: 35, carbs: 52, fat: 18 },
        dinner: { calories: 0, protein: 0, carbs: 0, fat: 0 }, // Noch nicht gegessen
        snacks: { calories: 150, protein: 8, carbs: 12, fat: 6 }
      },
      dailyGoals: {
        calories: 2200,
        protein: 165,
        carbs: 248,
        fat: 73
      },
      waterIntake: 1.8, // Liter
      waterGoal: 3.0,
      supplements: [
        { name: 'Whey Protein', taken: true, time: '07:30' },
        { name: 'Omega-3', taken: false, recommendedTime: '12:00' },
        { name: 'Multivitamin', taken: true, time: '08:00' }
      ]
    };
  }
}

export default NutritionAIService;

